/**
 * Button debounce (~30 ms) and auto-repeat while held: first repeat after 400 ms, then every 150 ms (SPEC §5.1).
 * Used for controller buttons and keyboard keys alike.
 */
export interface ButtonInputOptions {
  debounceMs: number;
  firstRepeatMs: number;
  repeatMs: number;
}

export const DEFAULT_BUTTON_OPTIONS: ButtonInputOptions = {
  debounceMs: 30,
  firstRepeatMs: 400,
  repeatMs: 150,
};

export class ButtonInput {
  private readonly lastChange = new Map<string, number>();
  private readonly down = new Set<string>();
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly raw = new Map<string, boolean>();
  private readonly settle = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(
    private readonly onFire: (button: string, repeat: boolean) => void,
    private readonly canRepeat: (button: string) => boolean = () => true,
    private readonly opts: ButtonInputOptions = DEFAULT_BUTTON_OPTIONS,
  ) {}

  /**
   * Feed a raw state. Duplicates are ignored. Flips inside the debounce window are deferred: when the window
   * ends, the latest raw state is applied — so a fast tap (press + release < 30 ms) never leaves a button stuck.
   */
  set(button: string, pressed: boolean, now = Date.now()): void {
    this.raw.set(button, pressed);
    const last = this.lastChange.get(button) ?? -Infinity;
    const wait = this.opts.debounceMs - (now - last);
    if (wait > 0) {
      if (!this.settle.has(button)) {
        this.settle.set(
          button,
          setTimeout(() => {
            this.settle.delete(button);
            this.apply(button, this.raw.get(button) ?? false, last + this.opts.debounceMs);
          }, wait),
        );
      }
      return;
    }
    this.apply(button, pressed, now);
  }

  private apply(button: string, pressed: boolean, now: number): void {
    const isDown = this.down.has(button);
    if (pressed === isDown) return;
    this.lastChange.set(button, now);
    if (pressed) {
      this.down.add(button);
      this.onFire(button, false);
      if (this.canRepeat(button)) {
        const tick = (delay: number) => {
          this.timers.set(
            button,
            setTimeout(() => {
              if (!this.down.has(button)) return;
              this.onFire(button, true);
              tick(this.opts.repeatMs);
            }, delay),
          );
        };
        tick(this.opts.firstRepeatMs);
      }
    } else {
      this.down.delete(button);
      const t = this.timers.get(button);
      if (t) clearTimeout(t);
      this.timers.delete(button);
    }
  }

  releaseAll(): void {
    for (const t of this.settle.values()) clearTimeout(t);
    this.settle.clear();
    for (const b of [...this.down]) this.apply(b, false, Date.now());
    this.raw.clear();
  }

  isDown(button: string): boolean {
    return this.down.has(button);
  }
}
